import { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { usePackagesState } from "@/store";
import appIcon from "@/assets/clippn-app-icon.png";
import { shouldOfferIosApp } from "./shouldOfferIosApp";
import { resolveIosAppLink } from "./iosAppLink";

/**
 * Invites iOS visitors to use the native app instead of the browser.
 *
 * Uploading from mobile Safari is the weakest path we have — the tab gets
 * suspended, the OS reclaims memory mid-transfer, and a long package is at the
 * mercy of whatever the user does next. The app keeps uploading in the
 * background and resumes across relaunches, so it's worth asking.
 *
 * Deliberately quiet: one dismissible card, remembered for a day, and
 * never on top of an upload that's already running.
 */

const DISMISS_KEY = "clippn.iosAppPrompt.dismissedAt";
const APPEAR_DELAY_MS = 1200;

const readDismissal = () => {
    try {
        return window.localStorage.getItem(DISMISS_KEY);
    } catch {
        return null;
    }
};

const rememberDismissal = () => {
    try {
        window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
        /* private mode — the prompt simply returns next visit */
    }
};

export const IosAppPrompt = () => {
    const { uploadingPackages } = usePackagesState();
    const [visible, setVisible] = useState(false);
    const [shown, setShown] = useState(false);

    const { url } = resolveIosAppLink();

    // The app is on the App Store, so the only question left is whether this
    // visitor should be asked — device, deployment, dismissal and whether an
    // upload is running. shouldOfferIosApp owns all four.
    const eligible = shouldOfferIosApp({
        userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent || "",
        maxTouchPoints: typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints || 0,
        dismissedAt: readDismissal(),
        uploadsInFlight: (uploadingPackages || []).length,
    });

    useEffect(() => {
        if (!eligible || shown) return undefined;

        const handle = setTimeout(() => {
            setShown(true);
            setVisible(true);
        }, APPEAR_DELAY_MS);

        return () => clearTimeout(handle);
    }, [eligible, shown]);

    const dismiss = useCallback(() => {
        setVisible(false);
        rememberDismissal();
    }, []);

    if (!eligible || !shown) return null;

    return (
        <Sheet isVisible={visible} role="dialog" aria-label="Use the Clippn Dashboard app">
            <Card>
                <Icon src={appIcon} alt="" aria-hidden="true" />

                <Copy>
                    <Title>Use the Clippn Dashboard app</Title>
                    <Body>
                        Uploads keep running in the background and pick up where they left
                        off — Safari can't do that on iPhone.
                    </Body>
                </Copy>

                <Actions>
                    <OpenLink href={url} target="_blank" rel="noreferrer" onClick={dismiss}>
                        Get the app
                    </OpenLink>

                    <DismissButton type="button" onClick={dismiss}>
                        Not now
                    </DismissButton>
                </Actions>

                <CloseButton type="button" onClick={dismiss} aria-label="Dismiss">
                    ×
                </CloseButton>
            </Card>
        </Sheet>
    );
};

const Sheet = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1400;

    padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
    pointer-events: ${({ isVisible }) => (isVisible ? "auto" : "none")};

    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transform: translateY(${({ isVisible }) => (isVisible ? "0" : "16px")});
    transition: opacity 220ms ease, transform 220ms ease;
`;

const Card = styled.div`
    position: relative;
    margin: 0 auto;
    width: min(560px, 100%);
    box-sizing: border-box;

    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
        "icon copy"
        "actions actions";
    gap: 12px;

    padding: 14px 16px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(20, 27, 40, 0.96);
    backdrop-filter: blur(10px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);

    @media (min-width: 560px) {
        grid-template-columns: auto 1fr auto;
        grid-template-areas: "icon copy actions";
        align-items: center;

        /* Room for the close button, which sits beside the action rather than
           on top of it once everything is on one line. */
        padding-right: 42px;
    }
`;

const Icon = styled.img`
    grid-area: icon;
    width: 46px;
    height: 46px;
    border-radius: 11px;
    display: block;
`;

const Copy = styled.div`
    grid-area: copy;
    min-width: 0;
    padding-right: 22px;
`;

const Title = styled.div`
    color: #f7fafa;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
`;

const Body = styled.div`
    margin-top: 3px;
    color: rgba(247, 250, 250, 0.62);
    font-size: 12px;
    line-height: 1.35;
`;

const Actions = styled.div`
    grid-area: actions;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const OpenLink = styled.a`
    flex: 1;
    text-align: center;
    white-space: nowrap;

    padding: 9px 16px;
    border-radius: 999px;
    background: #5d62b6;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;

    &:hover {
        background: #6c71c8;
    }
`;


const DismissButton = styled.button`
    flex: 1;
    white-space: nowrap;

    padding: 9px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: transparent;
    color: rgba(247, 250, 250, 0.75);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.06);
    }

    @media (min-width: 560px) {
        display: none;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 8px;
    right: 10px;

    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: rgba(247, 250, 250, 0.45);
    font-size: 20px;
    line-height: 1;
    cursor: pointer;

    &:hover {
        color: rgba(247, 250, 250, 0.8);
    }

    @media (min-width: 560px) {
        top: 50%;
        right: 8px;
        transform: translateY(-50%);
    }
`;
